<script setup lang="ts">
/**
 * BG §19.3 — the operational metadata voice: uppercase monospace, 0.06em, 12–14px.
 * `ENTERPRISE: INITIALISING`, `STATUS: PRIVATE_BETA`, `RUN: WAITING_ON_GATE`.
 *
 * Uppercasing is done in CSS rather than in the copy so screen readers announce words rather than
 * spelling out initialisms letter by letter.
 */
type Tone = 'default' | 'system' | 'muted'

withDefaults(
    defineProps<{
        /** `default` is dirty white, `system` cyan, `muted` grey — BG §19.3's three allowed colours. */
        tone?: Tone
        /** Rendered element. `p` by default; `span` when it sits inline in a larger block. */
        as?: string
    }>(),
    { tone: 'default', as: 'p' },
)
</script>

<template>
    <component :is="as" class="zh-system-label" :data-tone="tone">
        <slot />
    </component>
</template>

<style scoped>
.zh-system-label {
    margin: 0;
    font-family: var(--font-mono);
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.5;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    color: var(--dirty-white);
}

.zh-system-label[data-tone='system'] {
    color: var(--electric-cyan);
}

.zh-system-label[data-tone='muted'] {
    color: var(--muted-grey);
}

@media (min-width: 48rem) {
    .zh-system-label {
        font-size: 0.8125rem;
    }
}
</style>
